import { Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { formatManwon } from '../../utils/formatKRW';
import { logEvent } from '../../utils/ga';
import { ExpertCallout } from '../shared/ExpertCallout';
import { SeveranceForm } from './Form/SeveranceForm';
import {
  calculateSeverance,
  getSeveranceCallout,
  SeveranceFormValues,
  SeveranceResult,
} from './SeverancePage.hooks';
import styles from './SeverancePage.module.css';

function SeverancePage() {
  const resultRef = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState<SeveranceResult>();
  const [values, setValues] = useState<SeveranceFormValues>();

  const handleSubmit = useCallback((nextValues: SeveranceFormValues) => {
    logEvent('calculator', 'severance_calculate', 'submit');
    setValues(nextValues);
    setResult(calculateSeverance(nextValues));
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const callout = useMemo(() => (result ? getSeveranceCallout(result) : null), [result]);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>퇴직금 계산기 — 근속연수·평균임금 자동 계산 | 노후든든</title>
        <meta
          name="description"
          content="퇴직금 계산기로 예상 퇴직금을 5초 안에 계산하세요. 근속연수, 월급 입력만으로 법정 퇴직금과 세후 실수령액을 바로 확인합니다."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>퇴직금 계산기</h1>
          <p className={styles.pageDesc}>
            최근 급여와 근속 기간만 입력하면 법정 퇴직금, 추정 세금, 세후 실수령액을 빠르게 확인할 수 있습니다.
          </p>
        </div>
        <div className={styles.formCard}>
          <SeveranceForm onSubmit={handleSubmit} />
        </div>
      </Container>

      {result && values && (
        <div ref={resultRef}>
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              <div className={styles.heroCard}>
                <p className={styles.heroLabel}>세후 퇴직금</p>
                <p className={styles.heroAmount}>{formatManwon(result.netSeverance)}</p>
                <p className={styles.heroSub}>
                  근속 {values.workingYears}년 {values.workingMonths}개월 기준
                </p>
              </div>

              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>법정 퇴직금 (세전)</p>
                  <p className={styles.metricValue}>{formatManwon(result.legalSeverance)}</p>
                  <p className={styles.metricInterpretation}>평균임금과 근속일수 기준 추정치</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>퇴직소득세 (추정)</p>
                  <p className={styles.metricValue}>{formatManwon(result.taxAmount)}</p>
                  <p className={styles.metricInterpretation}>간이 세율 구간을 적용한 참고 금액</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>월 환산액</p>
                  <p className={styles.metricValue}>{formatManwon(result.monthlyEquivalent)}</p>
                  <p className={styles.metricInterpretation}>근속 기간으로 나눈 월 평균 퇴직금 효과</p>
                </div>
              </div>

              {callout && (
                <ExpertCallout title={callout.title} body={callout.body} cta={callout.cta} />
              )}

              <p className={styles.disclaimer}>
                이 계산기는 간이 추정값입니다. 실제 퇴직소득세는 근속연수공제·환산급여공제 등을 적용하여 산출하므로 차이가 있을 수 있습니다.
                정확한 금액은 국세청 홈택스 또는 세무사 상담을 이용하세요.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default SeverancePage;
